import React, { useState, useMemo, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Search, Plus, Save, Banknote, Calendar as CalendarIcon, User as UserIcon, ShoppingBag, Info, Package, Trash2, Building2, Store, PlusCircle, PenSquare, Eye, Edit2, Download, Filter, Trash, Tag as TagIcon, X, MapPin, Truck, RefreshCw, FileText, ShoppingCart, Percent, Warehouse } from "lucide-react";
import { CustomSelectInput } from "../../../components/CustomSelectInput";
import { CustomDateInput } from "../../../components/CustomDateInput";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { useCustomerStore } from "../../../store/customerStore";
import { useProductStore } from "../../../store/productStore";
import { useWarehouseStore } from "../../../store/warehouse";
import { toast } from "react-toastify";
import { Button } from "primereact/button";
import { RollTransactionModal } from "../../../hooks/RollTransactionModal";
import { usePermission } from "../../../hooks/usePermission";

// Tipler
import { Order, OrderPayload } from "../../../types/order";
import { OrderItemPayload } from "../../../types/orderItems";

interface OrderDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: any) => void; // Parent bileşendeki handleSave'i tetikler
    initialData: Order | null;
}

export const OrderDialog = ({ isOpen, onClose, onSave, initialData }: OrderDialogProps) => {
    const { fetchDataDynamic: fetchCustomers } = useCustomerStore();
    const { fetchDataDynamic: fetchStocks } = useProductStore();
    const { fetchDataDynamic: fetchWarehouses } = useWarehouseStore();

    const customerSelect = useSelectDialog<any>();
    const stockSelect = useSelectDialog<any>();
    const warehouseSelect = useSelectDialog<any>();

    const [isRollModalOpen, setIsRollModalOpen] = useState(false);
    const [selectedItemForRolls, setSelectedItemForRolls] = useState<any>(null);

    const { canRead: hasWarehouseAccess } = usePermission("warehouseModule");

    // Form State: UI isimlerini ve kalemleri yönetmek için genişletildi
    const [order, setOrder] = useState({
        accountId: null as number | null,
        accountName: "",
        warehouseId: null as number | null,
        warehouseName: "",
        orderDate: new Date(),
        deliveryDate: null as Date | null,
        currency: "TRY",
        description: "",
        reference: "",
        items: [] as any[]
    });

    // 1. Initial Data / Reset Mantığı
    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setOrder({
                    accountId: initialData.accountId,
                    accountName: initialData.account?.name || "",
                    warehouseId: initialData.warehouseId ?? null,
                    warehouseName: initialData.warehouse?.name || "Varsayılan Depo",
                    orderDate: new Date(initialData.orderDate),
                    deliveryDate: initialData.deliveryDate ? new Date(initialData.deliveryDate) : null,
                    currency: initialData.currency || "TRY",
                    description: initialData.description || "",
                    reference: initialData.externalId || "", // Varsa externalId'den çekebiliriz
                    items: initialData.items.map(item => ({
                        id: item.id, // Düzenleme için kritik
                        stockId: item.stockId,
                        variantId: item.variantId,
                        stockName: item.variant ? `${item.stock?.name} (${item.variant?.name})` : item.stock?.name,
                        quantity: item.quantity,
                        price: item.price,
                        taxRate: item.taxRate,
                        discountRate: item.discountRate || 0,
                        rollTransactions: item.rollTransactions || []
                    }))
                });
            } else {
                setOrder({
                    accountId: null, accountName: "", warehouseId: null, warehouseName: "",
                    orderDate: new Date(), deliveryDate: null, currency: "TRY",
                    description: "", reference: "", items: []
                });

                // Varsayılan depo yüklemesi
                if (hasWarehouseAccess) {
                    fetchWarehouses({ pageIndex: 1, pageSize: 5 }).then(res => {
                        if (res?.items?.length > 0) {
                            setOrder(prev => ({ ...prev, warehouseId: res.items[0].id, warehouseName: res.items[0].name }));
                        }
                    });
                }
            }
        }
    }, [isOpen, initialData]);

    // 2. Hesaplama Mantığı (Summary)
    const summary = useMemo(() => {
        let base = 0, tax = 0, discount = 0;
        order.items.forEach(item => {
            const lineBase = item.quantity * item.price;
            const lineDiscount = lineBase * (item.discountRate / 100);
            const lineTax = (lineBase - lineDiscount) * (item.taxRate / 100);
            base += lineBase;
            discount += lineDiscount;
            tax += lineTax;
        });
        return { base, tax, discount, total: base - discount + tax };
    }, [order.items]);

    // 3. Kaydetme Fonksiyonu (Fatura stilinde)
    const handleSave = () => {
        if (!order.accountId) { toast.error("Lütfen bir cari hesap seçiniz."); return; }
        if (order.items.length === 0) { toast.error("Siparişe en az bir ürün eklemelisiniz."); return; }
        if (hasWarehouseAccess && !order.warehouseId) { toast.error("Lütfen bir depo seçiniz."); return; }

        const payload: OrderPayload = {
            id: initialData?.id, // Düzenleme ise ID gönderilir
            accountId: order.accountId!,
            warehouseId: order.warehouseId,
            orderDate: order.orderDate,
            deliveryDate: order.deliveryDate,
            totalAmount: summary.total,
            currency: order.currency,
            description: order.description,
            // Backend'in beklediği item yapısı (taxAmount ve discountAmount dahil)
            items: order.items.map(item => {
                const base = item.quantity * item.price;
                const discAmt = base * (item.discountRate / 100);
                const taxAmt = (base - discAmt) * (item.taxRate / 100);

                const orderItemPayload: any = {
                    stockId: item.stockId,
                    variantId: item.variantId || null,
                    quantity: item.quantity,
                    price: item.price,
                    taxRate: item.taxRate,
                    taxAmount: taxAmt,
                    discountRate: item.discountRate,
                    discountAmount: discAmt,
                    total: base - discAmt + taxAmt,
                };

                if (item.rollTransactions && item.rollTransactions.length > 0) {
                    orderItemPayload.rollTransactions = item.rollTransactions;
                }
                return orderItemPayload;
            })
        };

        onSave(payload);
    };

    const addLine = async () => {
        const res = await fetchStocks({ pageIndex: 1, pageSize: 50 });
        const selected = await stockSelect.open({
            title: "Ürün / Varyant Seç",
            data: res.items,
            columns: [
                { field: "sku", header: "SKU" },
                { field: "name", header: "Ürün Adı" },
                { field: "quantity", header: "Miktar" }
            ],
            rowExpansionTemplate: (stock: any) => (
                <div className=" ">
                    <DataTable value={stock.variants} className="p-datatable-sm shadow-1   rounded-lg">
                        <Column field="name" header="Varyant" />
                        <Column field="sku" header="Barkod" />
                        <Column field="quantity" header="Miktar" />
                        <Column body={(variant) => (
                            <Button label="Seç" icon="pi pi-check" className="p-button-sm p-button-info"
                                onClick={() => stockSelect.selectItem({ ...stock, selectedVariant: variant })}
                            />
                        )} />
                    </DataTable>
                </div>
            )
        });

        if (selected) {
            const isVariant = !!selected.selectedVariant;
            const defaultPrice = selected.salePrice || 0;

            const newItem = {
                id: Math.random().toString(36).substr(2, 9),
                stockId: isVariant ? selected.selectedVariant.stockId : selected.id,
                variantId: isVariant ? selected.selectedVariant.id : null,
                stockName: isVariant ? `${selected.name} (${selected.selectedVariant.name})` : selected.name,
                quantity: 1,
                price: defaultPrice,
                taxRate: selected.taxRate || 20,
                discountRate: 0,
                rollTransactions: []
            };

            setOrder(prev => ({ ...prev, items: [...prev.items, newItem] }));
        }
    };

    const openRollsDialog = (row: any) => {
        if (!order.warehouseId) { toast.error("Lütfen önce bir depo seçiniz."); return; }
        setSelectedItemForRolls(row);
        setIsRollModalOpen(true);
    };

    const handleRollsComplete = (updatedRolls?: any[]) => {
        if (updatedRolls && selectedItemForRolls) {
            const totalQuantity = updatedRolls.reduce((acc, curr) => acc + (parseFloat(curr.quantity) || 0), 0);
            setOrder(prev => ({
                ...prev,
                items: prev.items.map(i => i.id === selectedItemForRolls.id ? { ...i, quantity: totalQuantity, rollTransactions: updatedRolls } : i)
            }));
        }
        setIsRollModalOpen(false);
        setSelectedItemForRolls(null);
    };

    return (
        <Dialog
            maximizable
            resizable={false}
            visible={isOpen} onHide={onClose} maximized modal
            header={
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-600 text-white shadow-md"><ShoppingBag size={20} /></div>
                    <div>
                        <h2 className="text-xl font-bold leading-none">{initialData ? "Siparişi Düzenle" : "Yeni Sipariş Oluştur"}</h2>
                        <p className="text-xs   mt-1">{order.accountName || "Cari seçimi bekleniyor..."}</p>
                    </div>
                </div>
            }
            footer={
                <div className="flex flex-col md:flex-row justify-between items-center w-full gap-4 p-2 text-text ">
                    <div className="flex flex-wrap gap-8 px-4">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-bold  ">ARA TOPLAM</span>
                            <span className="font-bold text-lg  ">{summary.base.toLocaleString('tr-TR', { style: 'currency', currency: order.currency })}</span>
                        </div>
                        <div className="flex flex-col text-red-500">
                            <span className="text-[10px] font-bold uppercase">İskonto</span>
                            <span className="font-bold text-lg">-{summary.discount.toLocaleString('tr-TR', { style: 'currency', currency: order.currency })}</span>
                        </div>
                        <div className="flex flex-col text-blue-600">
                            <span className="text-[10px] font-bold uppercase">KDV</span>
                            <span className="font-bold text-lg">+{summary.tax.toLocaleString('tr-TR', { style: 'currency', currency: order.currency })}</span>
                        </div>
                        <div className="flex flex-col text-green-700">
                            <span className="text-[10px] font-bold uppercase">Genel Toplam</span>
                            <span className="font-black text-2xl">{summary.total.toLocaleString('tr-TR', { style: 'currency', currency: order.currency })}</span>
                        </div>
                    </div>
                    <div className="flex gap-3 px-4">
                        <TextButton onClick={onClose}>Vazgeç</TextButton>
                        <Button label="Siparişi Kaydet" icon={<Save size={20} className="mr-2" />} onClick={handleSave} className="p-button-success shadow-lg" />
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-4 p-2">
                {/* Üst Bilgi Kartı */}
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-4   p-4 rounded-xl  card shadow-sm">

                    <div className="flex flex-col gap-1.5">
                        <CustomSelectInput
                            label={<><UserIcon size={12} /> Cari Hesap</>}
                            value={order.accountName || ""}
                            placeholder="Cari Seçiniz..."
                            icon={Search}
                            onClick={async () => {
                                const res = await fetchCustomers({ pageIndex: 1, pageSize: 20 });
                                const sel = await customerSelect.open({ title: "Cari Seç", data: res.items, columns: [{ field: "name", header: "Cari Adı" }] });
                                if (sel) setOrder(prev => ({ ...prev, accountId: sel.id, accountName: sel.name }));
                            }}
                            onClear={() => setOrder(prev => ({ ...prev, accountId: 0, accountName: "" }))}
                        />
                    </div>
                    {hasWarehouseAccess && (
                        <div className="flex flex-col gap-1.5">
                            <CustomSelectInput
                                label={<><Warehouse size={12} /> Depo</>}
                                value={order.warehouseName || ""}
                                placeholder="Depo Seçiniz..."
                                icon={Warehouse}
                                onClick={async () => {
                                    const res = await fetchWarehouses({ pageIndex: 1, pageSize: 20 });
                                    const sel: any = await warehouseSelect.open({ title: "Depo Seç", data: res.items, columns: [{ field: "name", header: "Depo" }] });
                                    if (sel) setOrder(prev => ({ ...prev, warehouseId: sel.id, warehouseName: sel.name }));
                                }}
                                onClear={() => setOrder(prev => ({ ...prev, warehouseId: 0, warehouseName: "" }))}
                            />
                        </div>
                    )}


                    <div className="flex flex-col gap-1.5">
                        <CustomDateInput
                            label={<><CalendarIcon size={12} /> Sipariş Tarihi</>}
                            value={order.orderDate}
                            onChange={(val) => setOrder(prev => ({ ...prev, orderDate: val as Date }))}
                            selectionMode="single"
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <CustomDateInput
                            label={<><CalendarIcon size={12} /> Teslimat Tarihi</>}
                            value={order.deliveryDate}
                            onChange={(val) => setOrder(prev => ({ ...prev, deliveryDate: val as Date }))}
                            selectionMode="single"
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] font-bold   flex items-center gap-1 uppercase"><Info size={12} /> Açıklama</label>
                        <InputText value={order.description} onChange={(e) => setOrder(prev => ({ ...prev, description: e.target.value }))} className="h-10" placeholder="Sipariş notu..." />
                    </div>
                </div>

                {/* Tablo Alanı */}
                <div className="flex-1 rounded-xl   overflow-hidden  card p-0 shadow-sm flex flex-col">
                    <div className="p-4    card rounded-b-none flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Package size={18} className=" " />
                            <span className="font-bold   tracking-tight">Sipariş Kalemleri</span>
                            <span className="bg-blue-100 text-blue-700 text-[10px] px-2 py-0.5 rounded-full font-bold">{order.items.length} Kalem</span>
                        </div>
                        <TextButton onClick={addLine} className="btn-new flex gap-2"><Plus size={18} />Satır Ekle</TextButton>
                    </div>

                    <DataTable
                        value={order.items}
                        className="p-datatable-sm"
                        emptyMessage={<div className="p-8 text-center   ">Henüz ürün eklenmedi. "Satır Ekle" butonu ile başlayın.</div>}
                    >
                        <Column header="ÜRÜN / VARYANT" body={(row) => (
                            <div className="flex flex-col gap-0.5">
                                <span className="font-semibold   leading-tight">{row.stockName}</span>
                                {row.variantId && <span className="text-[9px] font-bold text-orange-600 uppercase tracking-tighter">Varyantlı Ürün</span>}
                            </div>
                        )} />

                        <Column header="MİKTAR" style={{ width: '140px' }} body={(row) => (
                            <InputNumber
                                value={row.quantity}
                                onValueChange={(e) => setOrder(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, quantity: e.value } : i) }))}
                                buttonLayout="horizontal" min={1} inputClassName="w-full text-center font-bold " style={{ minWidth: "200px" }}
                            />
                        )} />

                        <Column header="BİRİM FİYAT" style={{ width: '160px' }} body={(row) => (
                            <InputNumber
                                value={row.price}
                                onValueChange={(e) => setOrder(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, price: e.value } : i) }))}
                                mode="currency" currency={order.currency} inputClassName="w-full text-right font-semibold" style={{ minWidth: "200px" }}
                            />
                        )} />

                        <Column header="İSK %" style={{ width: '100px' }} body={(row) => (
                            <InputNumber
                                value={row.discountRate}
                                onValueChange={(e) => setOrder(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, discountRate: e.value } : i) }))}
                                prefix="%" min={0} max={100} inputClassName="w-full text-center" style={{ minWidth: "100px" }}
                            />
                        )} />

                        <Column header="KDV %" style={{ width: '100px' }} body={(row) => (
                            <InputNumber
                                value={row.taxRate}
                                onValueChange={(e) => setOrder(prev => ({ ...prev, items: prev.items.map(i => i.id === row.id ? { ...i, taxRate: e.value } : i) }))}
                                prefix="%" inputClassName="w-full text-center" style={{ minWidth: "100px" }}
                            />
                        )} />

                        <Column header="TOPLAM" align="right" style={{ width: '150px' }} body={(row) => {
                            const total = (row.quantity * row.price) * (1 - row.discountRate / 100) * (1 + row.taxRate / 100);
                            return <span className="font-bold  ">{total.toLocaleString('tr-TR', { style: 'currency', currency: order.currency })}</span>
                        }} />

                        <Column style={{ width: '50px' }} body={(row) => (
                            <IconButton icon={Trash2} onClick={() => setOrder(prev => ({ ...prev, items: prev.items.filter(i => i.id !== row.id) }))} className="text-red-400 hover:text-red-600" />
                        )} />
                    </DataTable>
                </div>
            </div>

            {customerSelect.modal}
            {stockSelect.modal}
            {warehouseSelect.modal}

            {selectedItemForRolls && (
                <RollTransactionModal
                    isOpen={isRollModalOpen}
                    onClose={handleRollsComplete}
                    type="OUT"
                    initialRolls={selectedItemForRolls.rollTransactions || []}
                    stockId={selectedItemForRolls.stockId}
                    warehouseId={order.warehouseId!}
                    variantId={selectedItemForRolls.variantId}
                />
            )}
        </Dialog>
    );
};